import { FC } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PropsWithClassName } from '@/types/PropsWithClassName';
import { fadeInUp, staggerContainer } from '@/utils/motionVariants';
import { useTracking } from '@/hooks/useTracking';
import Routes from '@/router/Routes';

/**
 * Smooth scroll utility to scroll to top of page
 */
const smoothScrollToTop = () => {
  const heroSection = document.getElementById('hero-section');
  if (heroSection) {
    heroSection.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  } else {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }
};

/**
 * Footer Component
 */
const Footer: FC<PropsWithClassName> = ({ className = '' }) => {
  const { track } = useTracking();

  return (
    <>
      <motion.footer
        className={`${className}`}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={staggerContainer}
      >
        {/* Main Grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto_auto] gap-12 md:gap-16"
          variants={fadeInUp}
        >
          {/* Column 1: Logo + Social */}
          <div>
            <div className="mb-8">
              <p className="text-xs font-semibold tracking-[0.15em] uppercase text-white/40 mb-3">
                Follow
              </p>
              <a
                href="https://instagram.com/gympera.us"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track('landing.footer_social_click', { platform: 'instagram' })}
                className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"
                    clipRule="evenodd"
                  />
                </svg>
                @gymperafit
              </a>
            </div>

            <div className="mb-8">
              <p className="text-xs font-semibold tracking-[0.15em] uppercase text-white/40 mb-3">
                As Featured In
              </p>
              <a
                href="https://builtwithasmile.gumroad.com/l/BuiltWithASmileVol2DIGITAL?layout=profile"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-white/70 hover:text-white transition-colors"
              >
                Built With a Smile
              </a>
            </div>

            <button
              onClick={smoothScrollToTop}
              className="hidden md:block rounded p-1"
              aria-label="Gympera - Scroll to top"
            >
              <img
                src="/images/logo-03.png"
                alt="Gympera Logo"
                className="h-8 w-auto"
                loading="lazy"
              />
            </button>
          </div>

          {/* Column 2: For Users */}
          <div>
            <p className="text-xs font-semibold tracking-[0.15em] uppercase text-white/40 mb-5">
              For Users
            </p>
            <div className="mb-3">
              <Link to={Routes.REGISTER} className="text-sm text-white/70 hover:text-white transition-colors">
                Create Account
              </Link>
            </div>
            <div className="mb-3">
              <Link to="/gyms" className="text-sm text-white/70 hover:text-white transition-colors">
                Search
              </Link>
            </div>
            <div className="mb-3">
              <Link to={Routes.LOGIN} className="text-sm text-white/70 hover:text-white transition-colors">
                Book
              </Link>
            </div>
          </div>

          {/* Column 3: For Owners */}
          <div>
            <p className="text-xs font-semibold tracking-[0.15em] uppercase text-white/40 mb-5">
              For Owners
            </p>
            <div className="mb-3">
              <Link to={Routes.REGISTER_OWNER} className="text-sm text-white/70 hover:text-white transition-colors">
                List on Gympera
              </Link>
            </div>
            <div className="mb-3">
              <a href="mailto:support@gympera.com" className="text-sm text-white/70 hover:text-white transition-colors">
                Claim Your Listing
              </a>
            </div>
            <div className="mb-3">
              <Link to={Routes.OWNER_LISTING_STATUS} className="text-sm text-white/70 hover:text-white transition-colors">
                Owner Dashboard
              </Link>
            </div>
          </div>

          {/* Column 4: Company */}
          <div>
            <p className="text-xs font-semibold tracking-[0.15em] uppercase text-white/40 mb-5">
              Company
            </p>
            <div className="mb-3">
              <a href="mailto:support@gympera.com" className="text-sm text-white/70 hover:text-white transition-colors">
                Support
              </a>
            </div>
            <div className="mb-3">
              <a
                href="mailto:contact@gympera.com"
                onClick={() => track('landing.footer_email_click', {})}
                className="text-sm text-white/70 hover:text-white transition-colors"
              >
                Contact
              </a>
            </div>
          </div>
        </motion.div>

        {/* Bottom Bar */}
        <motion.div
          className="mt-2 pt-2 flex flex-col items-center justify-center gap-3 md:flex-row md:items-center md:justify-start"
          variants={fadeInUp}
        >
          <button
            onClick={smoothScrollToTop}
            className="md:hidden rounded p-1"
            aria-label="Gympera - Scroll to top"
          >
            <img
              src="/images/logo-03.png"
              alt="Gympera Logo"
              className="h-6 w-auto"
              loading="lazy"
            />
          </button>
          <p className="text-xs text-white/40">
            © 2026 Gympera LLC. All rights reserved.
          </p>
        </motion.div>
      </motion.footer>
    </>
  );
};

export default Footer;
